import { CloseButton } from "@headlessui/react";
import { Trans, t } from "@lingui/macro";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Dialog } from "@winamp/ui/components/Dialog/Dialog.js";
import { DialogActions } from "@winamp/ui/components/Dialog/DialogActions.js";
import { DialogBody } from "@winamp/ui/components/Dialog/DialogBody.js";
import { DialogTitle } from "@winamp/ui/components/Dialog/DialogTitle.js";
import { ErrorMessages } from "@winamp/ui/components/Fieldset/ErrorMessages.js";
import { Message } from "@winamp/ui/components/Message/Message.js";
import { MessageContent } from "@winamp/ui/components/Message/MessageContent.js";
import { MessageDescription } from "@winamp/ui/components/Message/MessageDescription.js";
import { type DependencyList, useEffect } from "react";
import type { ComponentProps } from "react";
import type * as v from "valibot";
import { useOptionalAppContext, useOptionalAuth } from "~/modules/auth/hooks";
import type { AuthContext } from "~/modules/auth/types";
import type { RetryableRenderProps } from "~/modules/retry/Retryable";
import { mailto } from "~/modules/support/mailto";

import { useFetcherWithReset } from "~/hooks/use-fetcher-with-reset";
import type { User } from "~/models/user";
import * as ArtistCollaboratorInvite from "./ArtistCollaboratorInvite/config";
import * as ArtistCollaboratorRolesCreate from "./ArtistCollaboratorRolesCreate/config";
import * as ArtistCollaboratorRolesEdit from "./ArtistCollaboratorRolesEdit/config";
import * as ArtistCollaboratorRolesRevoke from "./ArtistCollaboratorRolesRevoke/config";
import * as ArtistCreate from "./ArtistCreate/config";
import * as ArtistDelete from "./ArtistDelete/config";
import * as ArtistEdit from "./ArtistEdit/config";
import * as ArtistLinksLibrary from "./ArtistLinksLibrary/config";
import * as AudioDelete from "./AudioDelete/config";
import * as AudioDeleteBulk from "./AudioDeleteBulk/config";
import * as AudioDownloadBulk from "./AudioDownloadBulk/config";
import * as AudioEdit from "./AudioEdit/config";
import * as AudioEditBulk from "./AudioEditBulk/config";
import * as AudioPublishDisclaimer from "./AudioPublishDisclaimer/config";
import * as AudioPushToServiceBulk from "./AudioPushToServiceBulk/config";
import * as CollaboratorCreate from "./CollaboratorCreate/config";
import * as CollectibleCreate from "./CollectibleCreate/config";
import * as CollectionAppend from "./CollectionAppend/config";
import * as CollectionCreate from "./CollectionCreate/config";
import * as CollectionDelete from "./CollectionDelete/config";
import * as CollectionDeleteBulk from "./CollectionDeleteBulk/config";
import * as CollectionEdit from "./CollectionEdit/config";
import * as CollectionEntryDelete from "./CollectionEntryDelete/config";
import * as CollectionPushToServiceBulk from "./CollectionPushToServiceBulk/config";
import * as ContentIDCreate from "./ContentIDCreate/config";
import * as ContentIDCreateBulk from "./ContentIDCreateBulk/config";
import * as ContextSwitch from "./ContextSwitch/config";
import * as DistributionCreate from "./DistributionCreate/config";
import * as FanzoneAudioDelete from "./FanzoneAudioDelete/config";
import * as FanzoneAudioTierEdit from "./FanzoneAudioTierEdit/config";
import * as FanzoneCollectionDelete from "./FanzoneCollectionDelete/config";
import * as FanzoneCollectionTierEdit from "./FanzoneCollectionTierEdit/config";
import * as FanzonePostAnalyticsShow from "./FanzonePostAnalyticsShow/config";
import * as FanzonePostCommentsEdit from "./FanzonePostCommentsEdit/config";
import * as FanzonePostCreate from "./FanzonePostCreate/config";
import * as FanzonePostDelete from "./FanzonePostDelete/config";
import * as FanzonePostEdit from "./FanzonePostEdit/config";
import * as FanzonePostShow from "./FanzonePostShow/config";
import * as FanzonePostTierEdit from "./FanzonePostTierEdit/config";
import * as FanzoneProfileEdit from "./FanzoneProfileEdit/config";
import * as GlobalPreferencesEdit from "./GlobalPreferencesEdit/config";
import * as GlobalPreferencesThemeEdit from "./GlobalPreferencesThemeEdit/config";
import * as KYCStart from "./KYCStart/config";
import * as LabelCollaboratorInvite from "./LabelCollaboratorInvite/config";
import * as LabelCollaboratorRolesCreate from "./LabelCollaboratorRolesCreate/config";
import * as LabelCollaboratorRolesEdit from "./LabelCollaboratorRolesEdit/config";
import * as LabelCollaboratorRolesRevoke from "./LabelCollaboratorRolesRevoke/config";
import * as LabelCreate from "./LabelCreate/config";
import * as LabelEdit from "./LabelEdit/config";
import * as LicensingCreate from "./LicensingCreate/config";
import * as MembershipAudioCreate from "./MembershipAudioCreate/config";
import * as MembershipCollectionCreate from "./MembershipCollectionCreate/config";
import * as MembershipCreate from "./MembershipCreate/config";
import * as MembershipEdit from "./MembershipEdit/config";
import * as OnboardingArtistComplete from "./OnboardingArtistComplete/config";
import * as OnboardingLabelComplete from "./OnboardingLabelComplete/config";
import * as ProfileEdit from "./ProfileEdit/config";
import * as ProfileWallet from "./ProfileWallet/config";
import * as RevshareRuleExtend from "./RevshareRuleExtend/config";
import * as RightsCreate from "./RightsCreate/config";
import * as ServiceActivate from "./ServiceActivate/config";
import * as ServiceActivateRights from "./ServiceActivateRights/config";
import * as Subscribe from "./Subscribe/config";

/*
 * These dialogs are automatically registered in the app.
 * To trigger them, update the URL hash with the key of the dialog.
 * You can use the `getDialogRoute` function to get the dialog route in a Link or a `navigate`.
 */
export const routeDialogs = {
  [ArtistCollaboratorInvite.key]: {
    View: () => import("./ArtistCollaboratorInvite"),
    ...ArtistCollaboratorInvite,
  },
  [ArtistCollaboratorRolesCreate.key]: {
    View: () => import("./ArtistCollaboratorRolesCreate"),
    ...ArtistCollaboratorRolesCreate,
  },
  [ArtistCollaboratorRolesEdit.key]: {
    View: () => import("./ArtistCollaboratorRolesEdit"),
    ...ArtistCollaboratorRolesEdit,
  },
  [ArtistCollaboratorRolesRevoke.key]: {
    View: () => import("./ArtistCollaboratorRolesRevoke"),
    ...ArtistCollaboratorRolesRevoke,
  },
  [ArtistCreate.key]: {
    View: () => import("./ArtistCreate"),
    ...ArtistCreate,
  },
  [ArtistDelete.key]: {
    View: () => import("./ArtistDelete"),
    ...ArtistDelete,
  },
  [ArtistEdit.key]: {
    View: () => import("./ArtistEdit"),
    ...ArtistEdit,
  },
  [ArtistLinksLibrary.key]: {
    View: () => import("./ArtistLinksLibrary"),
    ...ArtistLinksLibrary,
  },
  [AudioDelete.key]: {
    View: () => import("./AudioDelete"),
    ...AudioDelete,
  },
  [AudioDeleteBulk.key]: {
    View: () => import("./AudioDeleteBulk"),
    ...AudioDeleteBulk,
  },
  [AudioDownloadBulk.key]: {
    View: () => import("./AudioDownloadBulk"),
    ...AudioDownloadBulk,
  },
  [AudioEdit.key]: {
    View: () => import("./AudioEdit"),
    ...AudioEdit,
  },
  [AudioEditBulk.key]: {
    View: () => import("./AudioEditBulk"),
    ...AudioEditBulk,
  },
  [AudioPublishDisclaimer.key]: {
    View: () => import("./AudioPublishDisclaimer"),
    ...AudioPublishDisclaimer,
  },
  [AudioPushToServiceBulk.key]: {
    View: () => import("./AudioPushToServiceBulk"),
    ...AudioPushToServiceBulk,
  },
  [CollaboratorCreate.key]: {
    View: () => import("./CollaboratorCreate"),
    ...CollaboratorCreate,
  },
  [CollectibleCreate.key]: {
    View: () => import("./CollectibleCreate"),
    ...CollectibleCreate,
  },
  [CollectionAppend.key]: {
    View: () => import("./CollectionAppend"),
    ...CollectionAppend,
  },
  [CollectionCreate.key]: {
    View: () => import("./CollectionCreate"),
    ...CollectionCreate,
  },
  [CollectionDelete.key]: {
    View: () => import("./CollectionDelete"),
    ...CollectionDelete,
  },
  [CollectionDeleteBulk.key]: {
    View: () => import("./CollectionDeleteBulk"),
    ...CollectionDeleteBulk,
  },
  [CollectionEdit.key]: {
    View: () => import("./CollectionEdit"),
    ...CollectionEdit,
  },
  [CollectionEntryDelete.key]: {
    View: () => import("./CollectionEntryDelete"),
    ...CollectionEntryDelete,
  },
  [CollectionPushToServiceBulk.key]: {
    View: () => import("./CollectionPushToServiceBulk"),
    ...CollectionPushToServiceBulk,
  },
  [ContentIDCreate.key]: {
    View: () => import("./ContentIDCreate"),
    ...ContentIDCreate,
  },
  [ContentIDCreateBulk.key]: {
    View: () => import("./ContentIDCreateBulk"),
    ...ContentIDCreateBulk,
  },
  [ContextSwitch.key]: {
    View: () => import("./ContextSwitch"),
    ...ContextSwitch,
  },
  [DistributionCreate.key]: {
    View: () => import("./DistributionCreate"),
    ...DistributionCreate,
  },
  [FanzoneAudioDelete.key]: {
    View: () => import("./FanzoneAudioDelete"),
    ...FanzoneAudioDelete,
  },
  [FanzoneAudioTierEdit.key]: {
    View: () => import("./FanzoneAudioTierEdit"),
    ...FanzoneAudioTierEdit,
  },
  [FanzoneCollectionDelete.key]: {
    View: () => import("./FanzoneCollectionDelete"),
    ...FanzoneCollectionDelete,
  },
  [FanzoneCollectionTierEdit.key]: {
    View: () => import("./FanzoneCollectionTierEdit"),
    ...FanzoneCollectionTierEdit,
  },
  [FanzonePostAnalyticsShow.key]: {
    View: () => import("./FanzonePostAnalyticsShow"),
    ...FanzonePostAnalyticsShow,
  },
  [FanzonePostCommentsEdit.key]: {
    View: () => import("./FanzonePostCommentsEdit"),
    ...FanzonePostCommentsEdit,
  },
  [FanzonePostCreate.key]: {
    View: () => import("./FanzonePostCreate"),
    ...FanzonePostCreate,
  },
  [FanzonePostDelete.key]: {
    View: () => import("./FanzonePostDelete"),
    ...FanzonePostDelete,
  },
  [FanzonePostEdit.key]: {
    View: () => import("./FanzonePostEdit"),
    ...FanzonePostEdit,
  },
  [FanzonePostShow.key]: {
    View: () => import("./FanzonePostShow"),
    ...FanzonePostShow,
  },
  [FanzonePostTierEdit.key]: {
    View: () => import("./FanzonePostTierEdit"),
    ...FanzonePostTierEdit,
  },
  [FanzoneProfileEdit.key]: {
    View: () => import("./FanzoneProfileEdit"),
    ...FanzoneProfileEdit,
  },
  [GlobalPreferencesEdit.key]: {
    View: () => import("./GlobalPreferencesEdit"),
    ...GlobalPreferencesEdit,
  },
  [GlobalPreferencesThemeEdit.key]: {
    View: () => import("./GlobalPreferencesThemeEdit"),
    ...GlobalPreferencesThemeEdit,
  },
  [KYCStart.key]: {
    View: () => import("./KYCStart"),
    ...KYCStart,
  },
  [LabelCollaboratorInvite.key]: {
    View: () => import("./LabelCollaboratorInvite"),
    ...LabelCollaboratorInvite,
  },
  [LabelCollaboratorRolesCreate.key]: {
    View: () => import("./LabelCollaboratorRolesCreate"),
    ...LabelCollaboratorRolesCreate,
  },
  [LabelCollaboratorRolesEdit.key]: {
    View: () => import("./LabelCollaboratorRolesEdit"),
    ...LabelCollaboratorRolesEdit,
  },
  [LabelCollaboratorRolesRevoke.key]: {
    View: () => import("./LabelCollaboratorRolesRevoke"),
    ...LabelCollaboratorRolesRevoke,
  },
  [LabelCreate.key]: {
    View: () => import("./LabelCreate"),
    ...LabelCreate,
  },
  [LabelEdit.key]: {
    View: () => import("./LabelEdit"),
    ...LabelEdit,
  },
  [LicensingCreate.key]: {
    View: () => import("./LicensingCreate"),
    ...LicensingCreate,
  },
  [MembershipAudioCreate.key]: {
    View: () => import("./MembershipAudioCreate"),
    ...MembershipAudioCreate,
  },
  [MembershipCollectionCreate.key]: {
    View: () => import("./MembershipCollectionCreate"),
    ...MembershipCollectionCreate,
  },
  [MembershipCreate.key]: {
    View: () => import("./MembershipCreate"),
    ...MembershipCreate,
  },
  [MembershipEdit.key]: {
    View: () => import("./MembershipEdit"),
    ...MembershipEdit,
  },
  [OnboardingArtistComplete.key]: {
    View: () => import("./OnboardingArtistComplete"),
    ...OnboardingArtistComplete,
  },
  [OnboardingLabelComplete.key]: {
    View: () => import("./OnboardingLabelComplete"),
    ...OnboardingLabelComplete,
  },
  [ProfileEdit.key]: {
    View: () => import("./ProfileEdit"),
    ...ProfileEdit,
  },
  [ProfileWallet.key]: {
    View: () => import("./ProfileWallet"),
    ...ProfileWallet,
  },
  [RevshareRuleExtend.key]: {
    View: () => import("./RevshareRuleExtend"),
    ...RevshareRuleExtend,
  },
  [RightsCreate.key]: {
    View: () => import("./RightsCreate"),
    ...RightsCreate,
  },
  [ServiceActivate.key]: {
    View: () => import("./ServiceActivate"),
    ...ServiceActivate,
  },
  [ServiceActivateRights.key]: {
    View: () => import("./ServiceActivateRights"),
    ...ServiceActivateRights,
  },
  [Subscribe.key]: {
    View: () => import("./Subscribe"),
    ...Subscribe,
  },
};

export type DialogKey = keyof typeof routeDialogs;

export type DialogSchema<K extends DialogKey> =
  K extends keyof typeof routeDialogs
    ? (typeof routeDialogs)[K] extends {
        Schema: infer S extends () => v.BaseSchema<
          unknown,
          unknown,
          v.BaseIssue<unknown>
        >;
      }
      ? v.InferInput<ReturnType<S>>
      : never
    : never;

export type DialogProps<TWrapped = false> = Omit<
  ComponentProps<typeof Dialog>,
  "children"
> & {
  hashParams: URLSearchParams;
  loadData?: () => void;
  reloading?: boolean;
} & (TWrapped extends true ? { loading: boolean } : { loading?: boolean });

export type PermissionViewProps = DialogProps;

export function DialogWithInvalidParameters({
  issues,
  ...props
}: Omit<DialogProps, "hashParams"> & { issues: Array<v.BaseIssue<unknown>> }) {
  return (
    <Dialog {...props}>
      <DialogTitle>
        <Trans>Invalid Parameters</Trans>
      </DialogTitle>
      <DialogBody>
        <ErrorMessages
          errors={issues?.map((issue) => {
            return `${issue.path?.map((item) => item.key).join(".")}: ${issue.message}`;
          })}
        />
      </DialogBody>
      <DialogActions>
        <CloseButton as={Button}>
          <Trans>Close</Trans>
        </CloseButton>
      </DialogActions>
    </Dialog>
  );
}

export function useDialogDataFetcher<TLoader>(
  path: string | undefined,
  {
    autoReset,
    deps,
    key,
    when,
  }: {
    autoReset?: boolean;
    key?: string;
    /**
     * When a dependency changes, the request is fetched again
     */
    deps?: DependencyList;
    /**
     * When should the request be fetched.
     */
    when?: boolean;
  } = {},
) {
  const dataFetcher = useFetcherWithReset<TLoader>({
    key: key ? `${key}-loader` : undefined,
  });

  function load() {
    if (path) {
      dataFetcher.load(path);
    }
  }

  useEffect(() => {
    if (when) {
      load();
    }
  }, deps ?? [when]);

  useEffect(() => {
    if (!when && autoReset && !!dataFetcher.data) {
      setTimeout(() => {
        dataFetcher.reset();
      }, 500);
    }
  }, [when]);

  return {
    data: dataFetcher.data,
    isLoading:
      !dataFetcher.data || (dataFetcher.state !== "idle" && !dataFetcher.data),
    load,
    reset: dataFetcher.reset,
    state: dataFetcher.state,
  };
}

function urlSearchParamsToObject(searchParams: URLSearchParams) {
  const obj = {};
  for (const [key, value] of searchParams.entries()) {
    Object.assign(obj, { [key]: value });
  }
  return obj;
}

export function dialogMailtoSupport(
  key: DialogKey,
  {
    context,
    auth,
    hashParams,
  }: {
    context?: AuthContext;
    auth?: { user: User };
    hashParams?: URLSearchParams;
  },
) {
  return mailto({
    to: "support@winamp.com",
    subject: t`Error loading data - ${key}`,
    body: [
      t`Dear Winamp Support Team,`,
      "",
      t`I'm experiencing issues while loading the "${key}" dialog.`,
      "",
      t`Could you please help me with this?`,
      "",
      t`Thanks in advance!`,
      "",
      "",
      "Here's some debug information for you: (do not modify this)",
      "",
      `- Artist UID: ${context?.artist?.uid ?? "N/A"}`,
      `- Label UID: ${context?.label?.uid ?? "N/A"}`,
      `- User UID: ${auth?.user.uid ?? "N/A"}`,
      `- Hash params: ${hashParams ? JSON.stringify(urlSearchParamsToObject(hashParams)) : "N/A"}`,
    ],
  });
}

export function DialogRetryPlaceholder({
  canRetry,
  dialogKey,
  isRetrying,
  hashParams,
  retries,
  retry,
}: Partial<RetryableRenderProps> &
  Pick<RetryableRenderProps, "retries" | "retry"> & {
    dialogKey: DialogKey;
    hashParams?: URLSearchParams;
  }) {
  const auth = useOptionalAuth();
  const context = useOptionalAppContext();

  return (
    <>
      <DialogBody>
        <Message type="error">
          <MessageContent>
            <MessageDescription>
              <Trans>
                It looks like some data was lost while loading this dialog.
                Please retry or contact the support if the issue persists.
              </Trans>
            </MessageDescription>
          </MessageContent>
        </Message>
      </DialogBody>
      <DialogActions>
        {retries > 0 && (
          <Button
            variant="secondary"
            loading={isRetrying}
            to={dialogMailtoSupport(dialogKey, {
              context,
              auth,
              hashParams,
            })}
            className="mr-auto"
          >
            <Trans>Contact Support</Trans>
          </Button>
        )}
        <CloseButton as={Button} variant="ghost">
          Close
        </CloseButton>
        {canRetry && (
          <Button loading={isRetrying} onClick={() => retry()}>
            <Trans>Retry</Trans>
          </Button>
        )}
      </DialogActions>
    </>
  );
}
